import React, { useState, useMemo, useEffect, useRef } from 'react';
import { collection, onSnapshot, query } from 'firebase/firestore';
import { db } from '../firebase';

// ── Category meta ─────────────────────────────────────────────────────────────

const CATEGORY_META = {
  foreclosure: { label: '🔥 Foreclosure', color: '#dc2626', bg: '#fff5f5', border: '#fecaca' },
  relocation:  { label: '🚛 Relocation',  color: '#2563eb', bg: '#eff6ff', border: '#bfdbfe' },
  downsizing:  { label: '🏠 Downsizing',  color: '#059669', bg: '#ecfdf5', border: '#a7f3d0' },
  divorce:     { label: '💔 Divorce',     color: '#7c3aed', bg: '#f5f3ff', border: '#ddd6fe' },
};

const CATEGORY_FILTERS = [
  { key: 'all',         label: 'All' },
  { key: 'foreclosure', label: '🔥 Foreclosure' },
  { key: 'relocation',  label: '🚛 Relocation' },
  { key: 'downsizing',  label: '🏠 Downsizing' },
  { key: 'divorce',     label: '💔 Divorce' },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

function parseCapturedAt(entry) {
  // capturedAt is set as an ISO string in grabSellers.js; scrapedAt is a Firestore Timestamp
  if (entry.capturedAt) {
    try { return new Date(entry.capturedAt); } catch {}
  }
  if (entry.scrapedAt?.toDate) {
    try { return entry.scrapedAt.toDate(); } catch {}
  }
  return null;
}

function toYMD(date) { return date.toLocaleDateString('en-CA'); }
function startOfDay(d) { const x = new Date(d); x.setHours(0,0,0,0); return x; }

const PRESETS = [
  { label: 'All time',  key: 'all' },
  { label: 'Today',     key: 'today' },
  { label: 'Yesterday', key: 'yesterday' },
  { label: 'Last 7d',   key: '7d' },
  { label: 'Last 30d',  key: '30d' },
  { label: 'Custom',    key: 'custom' },
];

function getPresetRange(key) {
  const now = new Date();
  const sod = startOfDay(now);
  if (key === 'today') return { from: sod, to: now };
  if (key === 'yesterday') {
    const s = new Date(sod); s.setDate(s.getDate() - 1);
    const e = new Date(sod); e.setMilliseconds(-1);
    return { from: s, to: e };
  }
  if (key === '7d')  { const s = new Date(sod); s.setDate(s.getDate() - 6);  return { from: s, to: now }; }
  if (key === '30d') { const s = new Date(sod); s.setDate(s.getDate() - 29); return { from: s, to: now }; }
  return null;
}

// ── Avatar ────────────────────────────────────────────────────────────────────

function Avatar({ src, name, size = 38 }) {
  const [err, setErr] = useState(false);
  const initials = name ? name.split(' ').map(w => w[0]).slice(0, 2).join('') : '?';
  const palette = ['#6366f1','#ec4899','#f59e0b','#10b981','#3b82f6','#8b5cf6','#ef4444','#14b8a6'];
  const bg = palette[name ? name.charCodeAt(0) % palette.length : 0];
  if (!src || err) return (
    <div style={{ width: size, height: size, borderRadius: '50%', flexShrink: 0, background: bg,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: size * 0.35, fontWeight: 700, color: '#fff', letterSpacing: '-0.5px' }}>
      {initials}
    </div>
  );
  return <img src={src} alt={name} onError={() => setErr(true)}
    style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />;
}

// ── Mini Calendar ─────────────────────────────────────────────────────────────

function Calendar({ from, to, onChange }) {
  const [viewYear, setViewYear] = useState(() => new Date().getFullYear());
  const [viewMonth, setViewMonth] = useState(() => new Date().getMonth());
  const [picking, setPicking] = useState('from');
  const [hovered, setHovered] = useState(null);

  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const firstDow    = new Date(viewYear, viewMonth, 1).getDay();

  function prev() {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
    else setViewMonth(m => m - 1);
  }
  function next() {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
    else setViewMonth(m => m + 1);
  }
  function pick(day) {
    const d = startOfDay(new Date(viewYear, viewMonth, day));
    if (picking === 'from') { onChange({ from: d, to: null }); setPicking('to'); }
    else {
      const end = new Date(d); end.setHours(23,59,59,999);
      onChange(d < from ? { from: d, to: new Date(from).setHours(23,59,59,999) && from } : { from, to: end });
      setPicking('from');
    }
  }
  function inRange(day) {
    const d = new Date(viewYear, viewMonth, day);
    const hi = to || hovered;
    if (!from || !hi) return false;
    return d >= (from < hi ? from : hi) && d <= (from < hi ? hi : from);
  }

  const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const DOW    = ['S','M','T','W','T','F','S'];

  return (
    <div>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12 }}>
        <button onClick={prev} style={calNavBtn}>‹</button>
        <span style={{ fontSize:13, fontWeight:600, color:'#111' }}>{MONTHS[viewMonth]} {viewYear}</span>
        <button onClick={next} style={calNavBtn}>›</button>
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:2, marginBottom:4 }}>
        {DOW.map((d,i) => <div key={i} style={{ textAlign:'center', fontSize:10, color:'#c0c0c0', fontWeight:700, padding:'2px 0' }}>{d}</div>)}
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:2 }}>
        {Array.from({length:firstDow}).map((_,i) => <div key={`b${i}`}/>)}
        {Array.from({length:daysInMonth},(_,i)=>i+1).map(day => {
          const ymd = toYMD(new Date(viewYear, viewMonth, day));
          const isF = from && ymd === toYMD(from);
          const isT = to   && ymd === toYMD(to);
          const inR = inRange(day);
          return (
            <div key={day} onClick={() => pick(day)}
              onMouseEnter={() => picking==='to' && setHovered(new Date(viewYear,viewMonth,day))}
              onMouseLeave={() => setHovered(null)}
              style={{ textAlign:'center', padding:'5px 2px', fontSize:12, borderRadius:5, cursor:'pointer',
                fontWeight: isF||isT ? 700 : 400,
                background: isF||isT ? '#111' : inR ? '#f0f0f0' : 'transparent',
                color: isF||isT ? '#fff' : '#333' }}>
              {day}
            </div>
          );
        })}
      </div>
      <p style={{ margin:'10px 0 0', fontSize:11, color:'#bbb', textAlign:'center' }}>
        {picking === 'from' ? 'Select start date' : 'Select end date'}
      </p>
    </div>
  );
}

const calNavBtn = { background:'none', border:'none', cursor:'pointer', fontSize:18, color:'#999', padding:'0 4px', lineHeight:1 };

// ── Message Button ────────────────────────────────────────────────────────────

const MSG_SERVER = process.env.REACT_APP_MSG_SERVER || 'http://localhost:3002';

function MessageButton({ profileUrl }) {
  const [status, setStatus] = useState('idle');
  const [errMsg, setErrMsg] = useState('');

  async function handleClick(e) {
    e.preventDefault();
    e.stopPropagation();
    if (status === 'loading') return;
    setStatus('loading');
    setErrMsg('');
    try {
      const res = await fetch(`${MSG_SERVER}/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ profileUrl }),
      });
      const data = await res.json();
      if (!res.ok || data.error) throw new Error(data.error || 'Unknown error');
      setStatus('done');
    } catch (err) {
      setStatus('error');
      setErrMsg(err.message.includes('fetch') ? 'Server offline' : err.message);
    }
  }

  if (status === 'done') return (
    <span style={{ fontSize:11, color:'#10b981', fontWeight:600, whiteSpace:'nowrap' }}>✓ Draft opened</span>
  );

  return (
    <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:2 }}>
      <button onClick={handleClick} disabled={status === 'loading'} style={{
        padding:'4px 10px', borderRadius:6, fontSize:11, fontWeight:600,
        cursor: status === 'loading' ? 'default' : 'pointer',
        border:'1.5px solid #d0d0d0',
        background: status === 'loading' ? '#f5f5f5' : '#fff',
        color: status === 'loading' ? '#bbb' : '#555',
        whiteSpace:'nowrap', transition:'all 0.1s',
      }}
        onMouseEnter={e => { if (status !== 'loading') e.target.style.borderColor = '#111'; }}
        onMouseLeave={e => { e.target.style.borderColor = '#d0d0d0'; }}
      >
        {status === 'loading' ? 'Opening…' : '✉ Message'}
      </button>
      {status === 'error' && <span style={{ fontSize:10, color:'#ef4444' }}>{errMsg}</span>}
    </div>
  );
}

// ── Category Badge ────────────────────────────────────────────────────────────

function CategoryBadge({ category, matchScore }) {
  const meta = CATEGORY_META[category] || { label: category, color:'#666', bg:'#f5f5f5', border:'#e5e5e5' };
  return (
    <span style={{
      fontSize:10, fontWeight:700, padding:'2px 7px', borderRadius:20,
      color: meta.color, background: meta.bg,
      border:`1px solid ${meta.border}`,
      whiteSpace:'nowrap', display:'inline-flex', alignItems:'center', gap:4,
    }}>
      {meta.label}
      {matchScore > 1 && (
        <span style={{ fontSize:9, opacity:0.7, fontWeight:600 }}>×{matchScore}</span>
      )}
    </span>
  );
}

// ── Lead Card ─────────────────────────────────────────────────────────────────

function LeadCard({ entry }) {
  const d = parseCapturedAt(entry);
  const dateStr = d ? d.toLocaleDateString('en-US', { month:'short', day:'numeric', year:'numeric' }) : '';
  const timeStr = d ? d.toLocaleTimeString('en-US', { hour:'numeric', minute:'2-digit' }) : '';
  const meta = CATEGORY_META[entry.category] || {};

  return (
    <div style={{
      display:'flex', gap:14, padding:'16px 20px',
      borderBottom:'1px solid #f3f3f3',
      borderLeft: `3px solid ${meta.border || 'transparent'}`,
      transition:'background 0.12s',
    }}
      onMouseEnter={e => e.currentTarget.style.background='#fafafa'}
      onMouseLeave={e => e.currentTarget.style.background='transparent'}
    >
      <a href={entry.profileUrl} target="_blank" rel="noopener noreferrer" style={{ flexShrink:0, marginTop:2 }}>
        <Avatar src={entry.profilePhoto} name={entry.name} size={40} />
      </a>
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:5, gap:8, flexWrap:'wrap' }}>
          <div style={{ display:'flex', alignItems:'center', gap:8, minWidth:0, flexWrap:'wrap' }}>
            <a href={entry.profileUrl} target="_blank" rel="noopener noreferrer"
              style={{ fontWeight:600, fontSize:14, color:'#0f0f0f', textDecoration:'none', whiteSpace:'nowrap' }}>
              {entry.name}
            </a>
            <CategoryBadge category={entry.category} matchScore={entry.matchScore} />
            {entry.state && (
              <span style={{ fontSize:11, color:'#666', fontWeight:600,
                background:'#f3f4f6', padding:'1px 7px', borderRadius:10 }}>
                {entry.stateAbbr || entry.state}
              </span>
            )}
            {entry.searchQuery && (
              <span style={{ fontSize:11, color:'#bbb', fontStyle:'italic' }}>
                via "{entry.searchQuery}"
              </span>
            )}
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:10, flexShrink:0 }}>
            <span style={{ fontSize:11, color:'#ccc' }}>{timeStr}{dateStr ? ` · ${dateStr}` : ''}</span>
            <MessageButton profileUrl={entry.profileUrl} />
          </div>
        </div>
        <p style={{ margin:0, fontSize:13.5, color:'#3a3a3a', lineHeight:1.6, whiteSpace:'pre-wrap' }}>
          {entry.postText}
        </p>
        {entry.postUrl && (
          <a href={entry.postUrl} target="_blank" rel="noopener noreferrer"
            style={{ display:'inline-block', marginTop:6, fontSize:11, color:'#9ca3af', textDecoration:'none' }}
            onMouseEnter={e => e.target.style.color = '#3b82f6'}
            onMouseLeave={e => e.target.style.color = '#9ca3af'}
          >
            View original post →
          </a>
        )}
      </div>
    </div>
  );
}

// ── Sidebar State Item ────────────────────────────────────────────────────────

function StateItem({ name, abbr, count, checked, onChange }) {
  return (
    <label style={{ display:'flex', alignItems:'center', gap:9, padding:'6px 12px', cursor:'pointer',
      borderRadius:6, transition:'background 0.1s', userSelect:'none' }}
      onMouseEnter={e => e.currentTarget.style.background='#f5f5f5'}
      onMouseLeave={e => e.currentTarget.style.background='transparent'}
    >
      <div onClick={onChange} style={{
        width:16, height:16, borderRadius:4, flexShrink:0,
        border: checked ? '2px solid #111' : '1.5px solid #d0d0d0',
        background: checked ? '#111' : '#fff',
        display:'flex', alignItems:'center', justifyContent:'center', transition:'all 0.1s',
      }}>
        {checked && <svg width="9" height="7" viewBox="0 0 9 7" fill="none">
          <path d="M1 3.5L3.5 6L8 1" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>}
      </div>
      <span style={{ fontSize:13, color:'#333', flex:1, lineHeight:1.3 }}>{name}</span>
      <span style={{ fontSize:10, color:'#aaa', fontWeight:600 }}>{abbr}</span>
      <span style={{ fontSize:11, color:'#bbb', fontVariantNumeric:'tabular-nums', minWidth:24, textAlign:'right' }}>{count}</span>
    </label>
  );
}

// ── Category Chips ────────────────────────────────────────────────────────────

function CategoryChips({ value, onChange, counts }) {
  return (
    <div style={{ display:'flex', gap:6, alignItems:'center', flexWrap:'wrap' }}>
      {CATEGORY_FILTERS.map(f => {
        const active = value === f.key;
        const count = counts[f.key] ?? 0;
        return (
          <button key={f.key} onClick={() => onChange(f.key)} style={{
            padding:'5px 11px', borderRadius:20, fontSize:12, fontWeight: active ? 700 : 500,
            cursor:'pointer', border: active ? '1.5px solid #111' : '1.5px solid #e5e5e5',
            background: active ? '#111' : '#fff',
            color: active ? '#fff' : '#555',
            whiteSpace:'nowrap', transition:'all 0.1s',
            display:'flex', alignItems:'center', gap:5,
          }}>
            {f.label}
            <span style={{ fontSize:10, opacity:0.65, fontVariantNumeric:'tabular-nums' }}>{count}</span>
          </button>
        );
      })}
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────

export default function FbSellerLeadsDashboard() {
  const [leads, setLeads]               = useState([]);
  const [loading, setLoading]           = useState(true);
  const [selectedStates, setSelStates]  = useState(new Set());
  const [search, setSearch]             = useState('');
  const [preset, setPreset]             = useState('all');
  const [customRange, setCustomRange]   = useState({ from: null, to: null });
  const [calOpen, setCalOpen]           = useState(false);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [sortMode, setSortMode]         = useState('score'); // 'score' | 'recent'
  const calRef = useRef(null);

  useEffect(() => {
    function down(e) { if (calRef.current && !calRef.current.contains(e.target)) setCalOpen(false); }
    document.addEventListener('mousedown', down);
    return () => document.removeEventListener('mousedown', down);
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(query(collection(db, 'fbSellerLeads')), snap => {
      setLeads(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return () => unsub();
  }, []);

  // Count leads per state (for sidebar)
  const stateCounts = useMemo(() => {
    const m = {};
    leads.forEach(l => {
      const key = l.stateAbbr || l.state;
      if (key) m[key] = (m[key] || 0) + 1;
    });
    return m;
  }, [leads]);

  const statesList = useMemo(() => {
    const set = new Map();
    leads.forEach(l => {
      const abbr = l.stateAbbr || (l.state || '').slice(0,2).toUpperCase();
      if (!abbr) return;
      if (!set.has(abbr)) set.set(abbr, { name: l.state || abbr, abbr });
    });
    return [...set.values()].sort((a,b) => a.name.localeCompare(b.name));
  }, [leads]);

  function toggleState(abbr) {
    setSelStates(prev => {
      const next = new Set(prev);
      next.has(abbr) ? next.delete(abbr) : next.add(abbr);
      return next;
    });
  }
  function clearStates() { setSelStates(new Set()); }
  function selectAllStates() { setSelStates(new Set(statesList.map(s => s.abbr))); }

  const dateRange = useMemo(() => {
    if (preset === 'custom') return customRange;
    return getPresetRange(preset);
  }, [preset, customRange]);

  const filtered = useMemo(() => {
    return leads
      .filter(l => {
        // State filter
        if (selectedStates.size > 0) {
          const abbr = l.stateAbbr || (l.state || '').slice(0,2).toUpperCase();
          if (!selectedStates.has(abbr)) return false;
        }
        // Search
        const q = search.toLowerCase();
        if (q && !l.name?.toLowerCase().includes(q) && !l.postText?.toLowerCase().includes(q)) return false;
        // Date
        if (dateRange?.from || dateRange?.to) {
          const d = parseCapturedAt(l);
          if (!d) return false;
          if (dateRange.from && d < dateRange.from) return false;
          if (dateRange.to   && d > dateRange.to)   return false;
        }
        // Category
        if (categoryFilter !== 'all' && l.category !== categoryFilter) return false;
        return true;
      })
      .sort((a, b) => {
        if (sortMode === 'score') {
          const diff = (b.matchScore || 0) - (a.matchScore || 0);
          if (diff !== 0) return diff;
        }
        return (parseCapturedAt(b) || 0) - (parseCapturedAt(a) || 0);
      });
  }, [leads, selectedStates, search, dateRange, categoryFilter, sortMode]);

  // Counts per category (for chips, respecting other active filters)
  const categoryCounts = useMemo(() => {
    const base = leads.filter(l => {
      if (selectedStates.size > 0) {
        const abbr = l.stateAbbr || (l.state || '').slice(0,2).toUpperCase();
        if (!selectedStates.has(abbr)) return false;
      }
      const q = search.toLowerCase();
      if (q && !l.name?.toLowerCase().includes(q) && !l.postText?.toLowerCase().includes(q)) return false;
      if (dateRange?.from || dateRange?.to) {
        const d = parseCapturedAt(l);
        if (!d) return false;
        if (dateRange.from && d < dateRange.from) return false;
        if (dateRange.to   && d > dateRange.to)   return false;
      }
      return true;
    });
    const m = { all: base.length, foreclosure: 0, relocation: 0, downsizing: 0, divorce: 0 };
    base.forEach(l => { if (m[l.category] !== undefined) m[l.category]++; });
    return m;
  }, [leads, selectedStates, search, dateRange]);

  const customLabel = useMemo(() => {
    if (!customRange.from) return 'Custom';
    const f = customRange.from.toLocaleDateString('en-US', { month:'short', day:'numeric' });
    if (!customRange.to) return `${f} →`;
    const t = customRange.to.toLocaleDateString('en-US', { month:'short', day:'numeric' });
    return `${f} – ${t}`;
  }, [customRange]);

  return (
    <div style={{ display:'flex', minHeight:'100vh', background:'#fff',
      fontFamily:'-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}>

      {/* ── Sidebar ── */}
      <div style={{ width:260, flexShrink:0, borderRight:'1px solid #efefef',
        background:'#fafafa', display:'flex', flexDirection:'column', minHeight:'100vh' }}>

        {/* Brand */}
        <div style={{ padding:'24px 20px 16px', borderBottom:'1px solid #efefef' }}>
          <div style={{ fontSize:15, fontWeight:700, color:'#111', letterSpacing:'-0.3px' }}>Seller Leads</div>
          <div style={{ fontSize:12, color:'#aaa', marginTop:2 }}>
            {loading ? '—' : `${leads.length} total · ${categoryCounts.foreclosure || 0} foreclosure`}
          </div>
        </div>

        {/* Date filters */}
        <div style={{ padding:'16px 12px 8px' }}>
          <div style={{ fontSize:10, fontWeight:700, color:'#bbb', letterSpacing:'0.08em',
            textTransform:'uppercase', padding:'0 4px', marginBottom:6 }}>Time Range</div>
          {PRESETS.map(p => {
            const active = preset === p.key;
            return (
              <div key={p.key} ref={p.key === 'custom' ? calRef : null} style={{ position:'relative' }}>
                <button
                  onClick={() => {
                    setPreset(p.key);
                    if (p.key === 'custom') setCalOpen(o => !o);
                    else setCalOpen(false);
                  }}
                  style={{
                    width:'100%', textAlign:'left', padding:'7px 12px', borderRadius:6,
                    border:'none', cursor:'pointer', fontSize:13, fontWeight: active ? 600 : 400,
                    background: active ? '#111' : 'transparent',
                    color: active ? '#fff' : '#555',
                    display:'flex', justifyContent:'space-between', alignItems:'center',
                  }}
                >
                  <span>{p.key === 'custom' ? customLabel : p.label}</span>
                  {p.key === 'custom' && <span style={{ fontSize:10, opacity:0.6 }}>▾</span>}
                </button>
                {p.key === 'custom' && calOpen && (
                  <div style={{ position:'absolute', left:'calc(100% + 8px)', top:0, zIndex:200,
                    background:'#fff', border:'1px solid #e8e8e8', borderRadius:10, padding:16,
                    boxShadow:'0 12px 40px rgba(0,0,0,0.12)', width:236 }}>
                    <Calendar from={customRange.from} to={customRange.to}
                      onChange={r => { setCustomRange(r); if (r.from && r.to) setCalOpen(false); }} />
                    {(customRange.from || customRange.to) && (
                      <button onClick={() => { setCustomRange({from:null,to:null}); setPreset('all'); setCalOpen(false); }}
                        style={{ marginTop:10, width:'100%', padding:'6px 0', fontSize:12, color:'#888',
                          background:'none', border:'1px solid #e5e5e5', borderRadius:6, cursor:'pointer' }}>
                        Clear dates
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* State filters */}
        <div style={{ padding:'12px 12px 0', flex:1, overflowY:'auto' }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
            padding:'0 4px', marginBottom:6 }}>
            <span style={{ fontSize:10, fontWeight:700, color:'#bbb', letterSpacing:'0.08em', textTransform:'uppercase' }}>
              States
            </span>
            <div style={{ display:'flex', gap:8 }}>
              <button onClick={selectAllStates} style={sidebarTextBtn}>All</button>
              <button onClick={clearStates} style={sidebarTextBtn}>None</button>
            </div>
          </div>
          {loading ? (
            <div style={{ padding:'12px', fontSize:12, color:'#ccc' }}>Loading…</div>
          ) : statesList.length === 0 ? (
            <div style={{ padding:'12px', fontSize:12, color:'#ccc' }}>No leads yet</div>
          ) : statesList.map(s => (
            <StateItem key={s.abbr} name={s.name} abbr={s.abbr} count={stateCounts[s.abbr] || 0}
              checked={selectedStates.has(s.abbr)} onChange={() => toggleState(s.abbr)} />
          ))}
        </div>

        {selectedStates.size > 0 && (
          <div style={{ padding:'12px 16px', borderTop:'1px solid #efefef' }}>
            <span style={{ fontSize:12, color:'#999' }}>{selectedStates.size} state{selectedStates.size > 1 ? 's' : ''} selected</span>
          </div>
        )}
      </div>

      {/* ── Main content ── */}
      <div style={{ flex:1, display:'flex', flexDirection:'column', minWidth:0 }}>

        {/* Topbar */}
        <div style={{ padding:'14px 24px', borderBottom:'1px solid #f3f3f3', display:'flex',
          flexDirection:'column', gap:10 }}>

          <div style={{ display:'flex', alignItems:'center', gap:12 }}>
            <input
              placeholder="Search post text or name…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ flex:1, maxWidth:380, padding:'7px 13px', border:'1px solid #e8e8e8',
                borderRadius:8, fontSize:13, color:'#111', background:'#fff', outline:'none' }}
            />

            <div style={{ display:'flex', borderRadius:7, border:'1px solid #e5e5e5', overflow:'hidden', flexShrink:0 }}>
              {[['score','Best Match'],['recent','Recent']].map(([key, label]) => (
                <button key={key} onClick={() => setSortMode(key)} style={{
                  padding:'6px 12px', fontSize:12, fontWeight: sortMode===key ? 700 : 400,
                  background: sortMode===key ? '#111' : '#fff',
                  color: sortMode===key ? '#fff' : '#777',
                  border:'none', cursor:'pointer', whiteSpace:'nowrap',
                }}>
                  {label}
                </button>
              ))}
            </div>

            <span style={{ fontSize:13, color:'#bbb', whiteSpace:'nowrap', flexShrink:0, marginLeft:'auto' }}>
              {loading ? '—' : `${filtered.length} lead${filtered.length !== 1 ? 's' : ''}`}
            </span>
          </div>

          <CategoryChips value={categoryFilter} onChange={setCategoryFilter} counts={categoryCounts} />
        </div>

        {/* Feed */}
        <div style={{ flex:1, overflowY:'auto' }}>
          {loading ? (
            <div style={{ padding:'80px 0', textAlign:'center', color:'#ddd', fontSize:14 }}>Loading…</div>
          ) : filtered.length === 0 ? (
            <div style={{ padding:'80px 0', textAlign:'center', color:'#ddd', fontSize:14 }}>
              No leads match your filters.
            </div>
          ) : filtered.map(entry => (
            <LeadCard key={entry.id} entry={entry} />
          ))}
        </div>
      </div>
    </div>
  );
}

const sidebarTextBtn = {
  background:'none', border:'none', cursor:'pointer',
  fontSize:11, color:'#bbb', padding:'1px 4px', borderRadius:4,
};
